import { DollarSign, Users, CreditCard } from 'lucide-react'
import { cn } from '@/lib/utils'

const steps = [
  {
    icon: DollarSign,
    title: 'Set Budget',
    description:
      'Define your monthly contribution per employee. Simple, transparent, and under your control.',
    step: 1,
  },
  {
    icon: Users,
    title: 'Sync Team',
    description:
      'Connect your payroll system with a single click. Your team roster syncs automatically.',
    step: 2,
  },
  {
    icon: CreditCard,
    title: 'Auto-Pay',
    description: 'We handle all payments and deductions. Set it and forget it.',
    step: 3,
  },
]

export const HowItWorks = () => {
  return (
    <section
      className="py-20 sm:py-28 lg:py-32 px-4 sm:px-6 lg:px-8 bg-white"
      id="how-it-works"
    >
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-14 sm:mb-16 lg:mb-20">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight text-neutral-900">
            Modern benefits in{' '}
            <span className="text-orange-primary">3 steps.</span>
          </h2>
        </div>

        <div className="grid sm:grid-cols-3 gap-8 sm:gap-6 lg:gap-12 relative">
          <div className="hidden sm:block absolute top-[72px] left-[calc(16.67%+40px)] right-[calc(16.67%+40px)] h-[2px] bg-gradient-to-r from-neutral-100 via-neutral-200 to-neutral-100" />

          {steps.map((item, index) => {
            const Icon = item.icon
            return (
              <div
                key={index}
                className={cn(
                  'group flex flex-col items-center text-center',
                  'p-6 sm:p-4 lg:p-6',
                  'rounded-2xl sm:rounded-3xl',
                  'bg-white',
                  'border border-transparent sm:border-neutral-100',
                  'sm:hover:border-orange-100',
                  'sm:hover:bg-orange-50/30',
                  'transition-all duration-300'
                )}
              >
                <div className="relative mb-6 sm:mb-8">
                  <div
                    className={cn(
                      'absolute -top-2 -right-2 z-10',
                      'w-7 h-7 sm:w-8 sm:h-8 rounded-full',
                      'bg-orange-primary text-white',
                      'text-xs sm:text-sm font-bold',
                      'flex items-center justify-center',
                      'shadow-lg shadow-orange-primary/30',
                      'ring-4 ring-white'
                    )}
                  >
                    {item.step}
                  </div>

                  <div
                    className={cn(
                      'w-20 h-20 sm:w-24 sm:h-24 lg:w-28 lg:h-28',
                      'rounded-2xl sm:rounded-3xl',
                      'bg-neutral-50',
                      'border border-neutral-100',
                      'flex items-center justify-center',
                      'transition-all duration-300 ease-out',
                      'group-hover:bg-orange-50',
                      'group-hover:border-orange-100',
                      'group-hover:scale-105',
                      'group-hover:shadow-lg group-hover:shadow-orange-100/50'
                    )}
                  >
                    <Icon
                      className={cn(
                        'w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12',
                        'text-orange-primary',
                        'transition-transform duration-300',
                        'group-hover:scale-110'
                      )}
                      strokeWidth={1.5}
                    />
                  </div>
                </div>

                <span className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.2em] text-orange-primary mb-2 sm:mb-3">
                  Step {item.step}
                </span>

                <h3 className="text-lg sm:text-xl font-bold mb-3 sm:mb-4 text-neutral-900">
                  {item.title}
                </h3>
                <p className="text-sm sm:text-base leading-relaxed text-neutral-500 max-w-[280px]">
                  {item.description}
                </p>
              </div>
            )
          })}
        </div>
      </div>
    </section>
  )
}
